import { Card, Button, Input, Modal, Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import Draggable from "react-draggable";

import styles from "../MasterManagement/index.module.less";
import Loading from "../common/Loading";
import { TableHelper } from "../../util/TableHelper";
import useDisplayQueueList from "./index.hook";
import CreateParking from "./CreatingParking";
import { Modules, Operations } from "../../constants/DatabaseConstants/MasterConstant";
import Authentication from "../../util/Authentication";
import LocalStorageUtil from "../../util/LocalStorageUtil";
import { LocalStorageConstants } from "../../constants/ApplicationConstants/LocalStorageConstants";
import { useReactDraggable } from "../../hooks/useReactDraggable";
import { ParkingRequestTypeConstant, ParkingStatusConstant } from "../../constants/DatabaseConstants/ParkingConstants";

import type { GetQueueList } from "../../shared/interface/parking.interface";
import type { UserInterface } from "../../shared/interface/auth/User.interface";

const rowClassName = (record: GetQueueList) => {
  if (record?.requestType.name === ParkingRequestTypeConstant.BOOKING) {
    return `${styles["highlighted-record-booking"]}`;
  } else if (record?.requestType.name === ParkingRequestTypeConstant.RETRIEVAL) {
    return `${styles["highlighted-record-retrieval"]}`;
  } else if (record?.requestType.name === ParkingRequestTypeConstant.STORAGE) {
    return `${styles["highlighted-record-storage"]}`;
  }

  return "";
};

const DisplayQueueStatus = () => {
  /* CONSTANT */
  const userData = LocalStorageUtil.localStorageGetItem(LocalStorageConstants.USER_DATA) as UserInterface;

  const config = useDisplayQueueList();

  /* HOOK */
  const { bounds, onStart } = useReactDraggable();

  const renderText = (text: string | number) => {
    return <div className="wordWrapWordBreak">{TableHelper.getValue(text)}</div>;
  };

  const renderNumberValue = (text: string | number) => {
    return <div className="gx-text-right">{TableHelper.getValue(text)}</div>;
  };

  const columns: ColumnsType<GetQueueList> = [
    {
      align: "left",
      title: "Sr No.",
      dataIndex: "sr_no",
      key: "sr_no",
      render: renderNumberValue,
      sorter: {
        compare: TableHelper.valueCompare("id")
      },
      width: "5%",
      onCell: (record) => ({
        className: rowClassName(record)
      })
    },
    {
      align: "left",
      title: "Username",
      dataIndex: "full_name",
      key: "full_name",
      render: (text, record) => <div className={record?.id === config.recordID?.id ? `wordWrapWordBreak gx-link entity-name-selected` : `wordWrapWordBreak`}>{TableHelper.getValue(text)}</div>,
      sorter: {
        compare: TableHelper.valueCompare("full_name")
      },
      onCell: (record) => ({
        className: rowClassName(record)
      })
    },
    {
      align: "left",
      title: "KeyFob",
      dataIndex: "key_fob_display_value",
      key: "key_fob_display_value",
      render: renderText,
      sorter: {
        compare: TableHelper.valueCompare("key_fob")
      },
      onCell: (record) => ({
        className: rowClassName(record)
      })
    },
    {
      align: "left",
      title: "Client",
      dataIndex: "client",
      key: "client",
      render: renderText,
      sorter: {
        compare: TableHelper.valueCompare("client")
      },
      onCell: (record) => ({
        className: rowClassName(record)
      })
    },
    {
      align: "left",
      title: "Request Type",
      dataIndex: "request_type_status",
      key: "request_type_status",
      render: renderText,
      sorter: {
        compare: TableHelper.valueCompare("request_type_status")
      },
      width: "10%",
      onCell: (record) => ({
        className: rowClassName(record)
      })
    },
    {
      align: "left",
      title: "Request Date",
      dataIndex: "request_date",
      key: "request_date",
      render: renderText,
      sorter: {
        compare: TableHelper.dateSorter("request_date")
      },
      onCell: (record) => ({
        className: rowClassName(record)
      })
    },
    {
      align: "left",
      title: "Scheduled Date",
      dataIndex: "scheduled_date_time",
      key: "scheduled_date_time",
      render: renderText,
      sorter: {
        compare: TableHelper.dateSorter("scheduled_date_time")
      },
      onCell: (record) => ({
        className: rowClassName(record)
      })
    },
    {
      align: "left",
      title: "Queue Number",
      dataIndex: "queue_number",
      key: "queue_number",
      render: renderNumberValue,
      sorter: {
        compare: TableHelper.valueCompare("queue_number")
      },
      width: "10%",
      onCell: (record) => ({
        className: rowClassName(record)
      })
    },
    {
      align: "left",
      title: "ETA",
      dataIndex: "eta",
      key: "eta",
      render: renderNumberValue,
      sorter: {
        compare: TableHelper.dateSorter("eta")
      },
      onCell: (record) => ({
        className: rowClassName(record)
      })
    },
    {
      align: "left",
      title: "Lift",
      dataIndex: "lift",
      key: "lift",
      render: renderNumberValue,
      sorter: {
        compare: TableHelper.valueCompare("lift")
      },
      onCell: (record) => ({
        className: rowClassName(record)
      })
    },
    {
      align: "left",
      title: "Queue Status",
      dataIndex: "queue_Status",
      key: "queue_Status",
      render(text, record) {
        const cellClassName = text === ParkingStatusConstant.EXECUTION ? `${styles["text-bold"]} ${styles["queue-status-cell"]} ${record?.requestType?.name === ParkingRequestTypeConstant.BOOKING ? styles["blinking-record-execution-booking"] : styles["blinking-record-execution-retrieval"]}` : `${styles["text-bold"]} ${rowClassName(record)}`;
        return {
          props: {
            className: cellClassName
          },
          children: <div>{TableHelper.getValue(text)}</div>
        };
      },
      sorter: {
        compare: TableHelper.valueCompare("queue_Status")
      },
      width: "10%"
    },
    {
      align: "left",
      title: "Action",
      fixed: "right",
      render: (text, record) => {
        const parkingStatusExpn = record?.requestType?.name === ParkingRequestTypeConstant.RETRIEVAL ? true : record?.parkingStatus?.name === ParkingStatusConstant.WAITING;

        const isCancelActionVisible = Authentication.hasModuleAndOperationAccess(userData, Modules.ONGOING_LIST, Operations.DELETE) && parkingStatusExpn;

        return (
          <Button
            onClick={() => config.handleCancel(record)}
            style={{
              visibility: isCancelActionVisible ? "visible" : "hidden",
              pointerEvents: isCancelActionVisible ? "all" : "none"
            }}
            className="gx-mb-0 button-gradiant"
          >
            Cancel
          </Button>
        );
      }
    }
  ];

  return (
    <Card
      className="gx-card card--height"
      title={
        <>
          <div className={`gx-title-container`}>
            <Button onClick={config.handleRefreshList} className={`button-gradiant gx-mb-0 add-button`} loading={config.loading}>
              Refresh
            </Button>

            <div className={`gx-title-right-section`}>
              <Input.Search className={`order-1 gx-mb-0 search-component__width`} allowClear placeholder={`Search...`} value={config.searchedText} onChange={(e) => config.handleChangeSearch(e.target.value)} onSearch={config.handleChangeOnSearch} />

              {Authentication.hasModuleAndOperationAccess(userData, Modules.ONGOING_LIST, Operations.CREATE) && (
                <Button onClick={config.handleAddClick} className={`order-2 button-gradiant gx-mb-0 add-button`}>
                  Add Request
                </Button>
              )}
            </div>
          </div>
        </>
      }
    >
      {config.loading && <Loading />}

      <CreateParking isVisible={config.isModalVisible} handleOnClose={config.handleOnClose} handleRefreshList={config.handleRefreshList} />

      {!config.loading && Authentication.hasModuleAndOperationAccess(userData, Modules.ONGOING_LIST, Operations.READ) && <Table className="gx-table-responsive" size="small" columns={columns} dataSource={config.queueList} pagination={config?.pagination} bordered onChange={config.handleChangePagination} rowKey={(record) => record.sr_no} />}

      <Modal
        title={<div className="modal-container">{"Cancel Confirmation"}</div>}
        open={config.isModalOpen}
        onOk={config.handleOk}
        onCancel={config.handleCancelModal}
        okText="Yes"
        cancelText="No"
        width="500px"
        wrapClassName="delete-modal"
        maskClosable={false}
        modalRender={(modal) => (
          <Draggable bounds={bounds} handle=".ant-modal-header" onStart={(event, uiData) => onStart(event as any, uiData)}>
            {modal}
          </Draggable>
        )}
        okButtonProps={{
          disabled: config?.saving,
          loading: config?.saving
        }}
      >
        <p>
          Are you sure you want to cancel the Queue request for user <span className="gx-link entity-name-selected">{config?.recordID?.user_name}?</span>
        </p>
      </Modal>
    </Card>
  );
};

export default DisplayQueueStatus;
